import { Injectable, signal } from "@angular/core";

/**
 * Global Loading Service
 *
 * Manages global loading state across the entire application.
 * Use this instead of local loading states in individual components.
 *
 * @example
 * ```typescript
 * constructor(private loading = inject(GlobalLoadingService)) {}
 *
 * loadData() {
 *   this.loading.show('Loading data...');
 *   this.apiService.get(url).subscribe({
 *     next: () => this.loading.hide(),
 *     error: () => this.loading.hide()
 *   });
 * }
 * ```
 */
@Injectable({
  providedIn: "root",
})
export class GlobalLoadingService {
  // Signals for reactive state
  private _isLoading = signal<boolean>(false);
  private _message = signal<string>("Loading...");

  // Read-only accessors
  isLoading = this._isLoading.asReadonly();
  message = this._message.asReadonly();

  /**
   * Show the global loading spinner (USE ONLY FOR GET REQUESTS)
   * For POST/PUT/DELETE, use local loading states in components
   * @param message Optional loading message to display
   */
  show(message: string = "Loading..."): void {
    this._message.set(message);
    this._isLoading.set(true);
  }

  /**
   * Hide the global loading spinner
   */
  hide(): void {
    this._isLoading.set(false);
    this._message.set("Loading...");
  }

  /**
   * Set loading state directly (USE ONLY FOR GET REQUESTS)
   * @param loading Whether to show or hide the spinner
   * @param message Optional loading message
   */
  setLoading(loading: boolean, message: string = "Loading..."): void {
    if (loading) {
      this.show(message);
    } else {
      this.hide();
    }
  }
}
